import { Menu } from 'lucide-react';

interface HeaderProps {
  connectionStatus: 'connecting' | 'connected' | 'retrying';
  updatedAt: string | null;
  sectionLabel?: string;
  onNavToggle?: () => void;
}

export function Header({
  connectionStatus,
  updatedAt,
  sectionLabel = 'HEATMAPS',
  onNavToggle,
}: HeaderProps) {
  const isLive = connectionStatus === 'connected';
  const statusLabel = isLive
    ? 'LIVE'
    : connectionStatus === 'retrying'
      ? 'RECONNECTING'
      : 'CONNECTING';
  const syncText = formatSyncTimestamp(updatedAt);

  return (
    <header className="flex h-14 leading-[normal] shrink-0 items-center gap-2.5 border-b border-line bg-background px-3 min-[721px]:h-[50px] min-[721px]:gap-4">
      {onNavToggle != null && (
        <button
          type="button"
          onClick={onNavToggle}
          aria-label="Open navigation"
          className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-md border border-line-strong bg-panel text-soft hover:bg-panel-2 hover:text-fg"
        >
          <Menu className="h-[18px] w-[18px]" strokeWidth={2} />
        </button>
      )}
      <div className="flex min-w-0 items-center gap-2 font-sans text-[17px] font-semibold tracking-[-0.01em] text-fg min-[721px]:gap-3 min-[721px]:text-xl">
        <img
          src="/brand/meridian-view-ai-mark.webp"
          alt=""
          aria-hidden="true"
          className="-mx-2.5 block h-6 w-auto shrink-0 mix-blend-lighten min-[721px]:-mx-3.5 min-[721px]:h-[30px]"
        />
        <span className="truncate">MeridianViewAI</span>
      </div>
      <span className="-ml-1.5 hidden font-mono text-xs tracking-[0.05em] text-dim min-[721px]:inline">
        / {sectionLabel}
      </span>

      <div className="ml-auto flex shrink-0 items-center gap-3">
        <span
          className={`inline-flex items-center gap-[7px] rounded-md border px-[9px] py-[5px] text-xs font-semibold tracking-[0.04em] min-[721px]:px-3 min-[721px]:py-1.5 ${
            isLive ? 'border-up/20 bg-up/[.08] text-up' : 'border-down/25 bg-down/[.08] text-down'
          }`}
        >
          <i
            className={`h-[7px] w-[7px] rounded-full ${
              isLive ? 'bg-up shadow-[0_0_8px_#22c55e]' : 'bg-down shadow-[0_0_8px_#ef4444]'
            }`}
          />
          {statusLabel}
        </span>

        {syncText != null && (
          <span className="hidden font-mono text-[11px] text-dim min-[480px]:inline">
            {syncText}
          </span>
        )}
      </div>
    </header>
  );
}

function formatSyncTimestamp(updatedAt: string | null): string | null {
  if (updatedAt == null) {
    return null;
  }

  const parsed = new Date(updatedAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return `Last Synced At ${new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(parsed)}`;
}
